import { innerSearch } from 'api/Api';
import PayverseDirectHeader from 'app/components/PayverseDirectHeader';
import { usePaymentState } from 'payment_state/PaymentStateContext';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { findImage } from 'utils/Utils';

interface ReturnForm {
  resultStatus: string;
  resultMessage: string;
  resultCode: string;
  tid: string;
  orderId: string;
  mallReserved: string;
}

const DirectPendingPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const { paymentState, setPaymentState } = usePaymentState();
  const [isSuccess, setIsSuccess] = useState(false);
  const [tid, setTid] = useState<string>();

  // InnerSearch 실행
  const transactResult = useMutation(() => innerSearch(tid), {
    onSuccess: data => {
      //Data Set
      const returnUrl = data.data.returnUrl;
      const payType = String(data.data.display.payType).toLowerCase();
      const resultStatus = String(
        data.data.response.resultStatus,
      ).toLocaleLowerCase();
      const descriptor = data.data.display.descriptor;
      // Result Form Set
      const resultForm = {
        resultStatus: data.data.response.resultStatus,
        resultMessage: data.data.response.resultMessage,
        resultCode: data.data.response.resultCode,
        tid: data.data.response.tid,
        orderId: data.data.response.orderId,
        mallReserved: data.data.response.mallReserved,
        schemeGroup: data.data.response.schemeGroup,
        schemeCode: data.data.response.schemeCode,
      };
      // PaymentState Set
      setPaymentState(prevState => ({
        ...prevState,
        resultMessage: resultForm.resultMessage,
        resultCode: resultForm.resultCode,
        tid: resultForm.tid,
        orderId: resultForm.orderId,
        mallReserved: resultForm.mallReserved,
        returnUrl: returnUrl,
        descriptor: descriptor,
        schemeGroup: resultForm.schemeGroup,
        schemeCode: resultForm.schemeCode,
      }));
      if (
        data.data.response.resultStatus === null ||
        data.data.response.resultStatus === undefined
      ) {
        navigate('/error');
      }
      // Pending 상태가 다른상태로 변경될때 FormSubmit
      if (resultStatus === 'pending' || resultStatus === 'processing') {
        if (payType === 'vbank' || payType === 'otc') {
          createFormAndSubmit(resultForm, returnUrl);
        } else {
          navigate('/directPending');
        }
      } else {
        createFormAndSubmit(resultForm, returnUrl);
      }
    },
    onError: error => {
      navigate('/error');
    },
  });

  //취소 버튼 시에 1회성으로 InnerSearch 돌리기 위함
  const oneTimeTransactResult = useMutation(() => innerSearch(tid), {
    onSuccess: data => {
      //Data Set
      const returnUrl = data.data.returnUrl;
      const payType = String(data.data.display.payType).toLowerCase();
      const resultStatus = String(
        data.data.response.resultStatus,
      ).toLocaleLowerCase();
      const descriptor = data.data.display.descriptor;
      // Result Form Set
      const resultForm = {
        resultStatus: data.data.response.resultStatus,
        resultMessage: data.data.response.resultMessage,
        resultCode: data.data.response.resultCode,
        tid: data.data.response.tid,
        orderId: data.data.response.orderId,
        mallReserved: data.data.response.mallReserved,
        schemeGroup: data.data.response.schemeGroup,
        schemeCode: data.data.response.schemeCode,
      };
      // PaymentState Set
      setPaymentState(prevState => ({
        ...prevState,
        resultMessage: resultForm.resultMessage,
        resultCode: resultForm.resultCode,
        tid: resultForm.tid,
        orderId: resultForm.orderId,
        mallReserved: resultForm.mallReserved,
        returnUrl: returnUrl,
        descriptor: descriptor,
        schemeGroup: resultForm.schemeGroup,
        schemeCode: resultForm.schemeCode,
      }));
      if (
        data.data.response.resultStatus === null ||
        data.data.response.resultStatus === undefined
      ) {
        navigate('/error');
      }
      // Pending 상태가 다른상태로 변경될때 FormSubmit
      if (resultStatus === 'pending' || resultStatus === 'processing') {
        if (payType === 'vbank' || payType === 'otc') {
          createFormAndSubmit(resultForm, returnUrl);
        } else {
          handleAllClose();
        }
      } else {
        createFormAndSubmit(resultForm, returnUrl);
      }
    },
    onError: error => {
      navigate('/error');
    },
  });

  useEffect(() => {
    const browserTypeJson = (lang: string) => {
      switch (lang) {
        case 'zh-TW':
          return 'cntr';
        case 'zh-CN':
          return 'cnsi';
        case 'en-US':
          return 'en';
        case 'ko':
          return 'ko';
        case 'ko-KR':
          return 'ko';
        case 'ja':
          return 'jp';
        default:
          return '';
      }
    };
    const userLanguage = navigator.language;
    const browserLanguage = browserTypeJson(userLanguage);
    if (browserLanguage !== '') {
      i18n.changeLanguage(browserLanguage);
      return;
    } else {
      i18n.changeLanguage('en');
    }
  }, []);

  useEffect(() => {
    if (paymentState.tid !== null) {
      setTid(paymentState.tid);
      setIsSuccess(true);
    }
  }, [paymentState]);

  useEffect(() => {
    if (isLoading) {
      setTimeout(() => {
        if (isSuccess) {
          setIsLoading(false);
          return;
        }
        setIsLoading(false);
      }, 3000);
    }
  }, [isLoading, isSuccess]);

  const handleClickInnerSearch = e => {
    e.preventDefault();
    setIsLoading(true);
    transactResult.mutate();
  };

  const handleInnerSearchAndClose = e => {
    e.preventDefault();
    setIsLoading(true);
    oneTimeTransactResult.mutate();
  };

  const handleAllClose = () => {
    const returnObject = {
      resultStatus: 'PENDING',
      resultCode: paymentState.resultCode,
      resultMessage: paymentState.resultMessage,
      tid: paymentState.tid,
      orderId: paymentState.orderId,
      mallReserved: paymentState.mallReserved,
      schemeGroup: paymentState.schemeGroup,
      schemeCode: paymentState.schemeCode,
    };

    if (paymentState.returnUrl) {
      createFormAndSubmit(returnObject, paymentState.returnUrl);
    }
  };

  const createFormAndSubmit = (formData, returnUrl) => {
    const form = document.createElement('form');
    form.setAttribute('method', 'POST');
    form.setAttribute('action', `${returnUrl}`);
    form.style.display = 'none';
    Object.entries(formData).forEach(([key, value]) => {
      const input = document.createElement('input');
      input.setAttribute('type', 'hidden');
      input.setAttribute('name', key);
      input.setAttribute('value', String(value));
      form.appendChild(input);
    });
    document.body.appendChild(form);
    form.submit();
  };

  return (
    <div className="payverse-wrap">
      <div className="payverse">
        <PayverseDirectHeader />
        {isLoading && (
          <div className="white-loading-section">
            <div className="white-spin" />
          </div>
        )}
        <div className="completed">
          <div className="middle">
            <div className="icon">
              <img src={findImage('error.png')} />
              {/* <img src={findImage('error.png')} /> */}
            </div>
            <div className="title">{t('payment_decline_uc')}</div>
            <div className="text" style={{ whiteSpace: 'pre-line' }}>
              {t('payment_confirm_msg')}
            </div>
          </div>
        </div>
        <div className="c-section">
          {!paymentState.resultCode ? (
            <div className="box confirmBox" style={{ marginLeft: '30%' }}>
              <button className="orange" onClick={handleClickInnerSearch}>
                {t('payment_confirm')}
              </button>
            </div>
          ) : (
            <div className="box confirmBox">
              <button className="orange" onClick={handleClickInnerSearch}>
                {t('payment_confirm')}
              </button>
              <button className="gray" onClick={handleInnerSearchAndClose}>
                {t('cancel')}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DirectPendingPage;
